import { ColumnState } from '@ag-grid-community/core'
import { FilterModel } from '@helios/server-side-util'
import { FlexPositionsExportRequest } from '../../models/flex-positions-export.model'
import { ColumnCatalogEntry } from '../../models/source-definition.model'
import { QueryFieldMeta } from './field-types'
import {
  buildQueryRequest,
  GridSortModelEntry,
  MANDATORY_QUERY_FIELDS,
} from './query-request.builder'

export interface ExportModelInput {
  /** ag-Grid's live filter model, keyed by colId. */
  filterModel: FilterModel<unknown>
  /** ag-Grid's live sort model, in user-chosen precedence order (see {@link sortModelFromColumnState}). */
  sortModel: readonly GridSortModelEntry[]
  /** The DISPLAYED colIds, in on-screen order. */
  displayed: readonly string[]
  /** The current source's resolved catalog columns — the source of every header label. */
  catalog: readonly ColumnCatalogEntry[]
  /** Per-field query metadata for that catalog, from `buildFieldMeta` (overrides applied). */
  meta: ReadonlyMap<string, QueryFieldMeta>
  /** The active match-count cross-filter's row ids, or `null` when none is active. */
  crossFilterIds: readonly string[] | null
  /** The browser's IANA time zone. */
  userTimeZone: string
}

/**
 * Fields the grid query always projects for the UI's own use, never for display. `rowId` is the
 * hidden identity column and `availableActions` is row metadata; either one in `fields` would add a
 * column nobody sees on screen to the workbook.
 */
const NON_EXPORTABLE_FIELDS: ReadonlySet<string> = new Set(
  MANDATORY_QUERY_FIELDS,
)

/**
 * Build a POST /flex-positions/export body from the grid's live state, or `null` when no displayed
 * column can be exported (in which case no request should be sent at all — the backend 400s an empty
 * `fields`).
 *
 * `filter`, `sort` and `context` come from {@link buildQueryRequest} — the same pure function the
 * grid's own block datasource calls — so the file holds exactly the rows the grid is showing: the same
 * column filters, the same status chips, the same match-count cross-filter, the same sort. Paging is
 * the one thing deliberately NOT carried over; the server applies its own export cap.
 *
 * The columns differ from the /query projection in two ways:
 *  - only DISPLAYED columns the catalog knows are exported, in on-screen order. UI-owned columns
 *    (`calc:` unit differences, computed Helios columns, the per-target match counts) are unknown to
 *    the backend and are dropped, exactly as `buildQueryRequest` drops them;
 *  - the always-projected UI fields (`rowId`, `availableActions`) are left out, because they are not
 *    on screen.
 *
 * `headers` is the catalog's own `headerName` for each field, one per field. Not the grid header:
 * for an Aggregate Info column the grid appends the chosen type's glyph (`col-def-factory`), which
 * is UI chrome and does not belong in a spreadsheet. For every other column the two are identical.
 */
export function buildExportRequest(
  input: ExportModelInput,
): FlexPositionsExportRequest | null {
  const { catalog, meta, displayed } = input

  const headerByField = new Map<string, string>()
  catalog.forEach(column => headerByField.set(column.field, column.headerName))

  const fields: string[] = []
  const headers: string[] = []
  const claimed = new Set<string>()
  displayed.forEach(field => {
    if (claimed.has(field)) return
    if (NON_EXPORTABLE_FIELDS.has(field)) return
    if (!meta.has(field)) return
    claimed.add(field)
    fields.push(field)
    // A blank catalog label would leave an unlabelled column; the field id is the backend's own default.
    const label = headerByField.get(field)
    headers.push(label ? label : field)
  })

  if (!fields.length) return null

  const query = buildQueryRequest({
    // Paging is replaced server-side by the export cap; these never reach the export body.
    startRow: 0,
    endRow: 0,
    filterModel: input.filterModel,
    sortModel: input.sortModel,
    projection: fields,
    meta,
    crossFilterIds: input.crossFilterIds,
  })

  return {
    fields,
    headers,
    filter: query.filter,
    sort: query.sort,
    // The aggInfo hint is computed over the query's fields + filter + sort. The query's fields are the
    // export's plus the mandatory UI fields, none of which is AGG_INFO-stored, so the hint is the same.
    context: query.context,
    userTimeZone: input.userTimeZone,
  }
}

/**
 * The grid's sort model, read back from its column state in user-chosen precedence order.
 *
 * Outside a `getRows` call there is no `request.sortModel` to hand, so the export reconstructs it the
 * way ag-Grid itself does: every column with a `sort`, ordered by `sortIndex`. A column with no
 * `sortIndex` keeps its column-state position (stable sort), which is ag-Grid's own fallback.
 */
export function sortModelFromColumnState(
  state: readonly ColumnState[] | null | undefined,
): GridSortModelEntry[] {
  return (state ?? [])
    .map((column, position) => ({ column, position }))
    .filter(({ column }) => column.sort === 'asc' || column.sort === 'desc')
    .sort(
      (a, b) =>
        (a.column.sortIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.column.sortIndex ?? Number.MAX_SAFE_INTEGER) ||
        a.position - b.position,
    )
    .map(({ column }) => ({
      colId: column.colId as string,
      sort: column.sort as 'asc' | 'desc',
    }))
}
